import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

const ROLES_2FA_OBLIGATOIRE = new Set(['admin', 'arms_seller']);

// ── Traductions ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Sécurité',
    retour: '← Mon profil',
    chargement: 'Chargement…',

    s1titre: 'Mot de passe',
    labelAncien: 'Mot de passe actuel',
    labelNouveau: 'Nouveau mot de passe',
    labelConfirmer: 'Confirmer le nouveau mot de passe',
    btnChanger: 'Changer le mot de passe',
    chargementMdp: 'Mise à jour…',
    succesMdp: 'Mot de passe mis à jour avec succès.',
    errMdpCorrespond: 'Les mots de passe ne correspondent pas.',
    errMdpCourt: 'Le nouveau mot de passe doit contenir au moins 8 caractères.',

    s2titre: 'Authentification à deux facteurs (2FA)',
    badge2FAActif: '✓ 2FA activé',
    badge2FAInactif: '2FA désactivé',
    desc2FAInactif:
      "Protégez votre compte avec une couche de sécurité supplémentaire. Après activation, chaque connexion nécessitera un code de votre application d'authentification.",
    desc2FAActif:
      'Votre compte est protégé par une application TOTP (Google Authenticator, Authy, etc.).',
    obligatoire: '⚠ Obligatoire pour ce rôle — ne peut pas être désactivé.',
    btnActiver: 'Activer le 2FA',
    btnDesactiver: 'Désactiver le 2FA',
    btnAnnuler: 'Annuler',

    instrQR:
      'Scannez ce QR code avec votre application TOTP (Google Authenticator, Authy, 1Password, etc.).',
    instrQRSuite:
      'Votre application affichera un code à 6 chiffres qui se renouvelle toutes les 30 secondes.',
    btnJaiScan: "J'ai scanné le code →",
    instrCode:
      "Entrez le code à 6 chiffres affiché dans votre application pour confirmer l'activation.",
    placeholder2FA: 'Code à 6 chiffres',
    btnActiver2FA: 'Activer',

    titreCodesOk: '2FA activé !',
    instrCodesOk:
      "Conservez ces codes de récupération en lieu sûr — de préférence hors ligne. Chaque code ne peut être utilisé qu'une seule fois si vous perdez accès à votre application.",
    btnTerminer: 'Terminer',

    instrDesactiver:
      'Entrez le code actuellement affiché dans votre application pour désactiver le 2FA.',
    btnConfirmerDesactiver: 'Confirmer la désactivation',
    desactivation2FASucces: 'Authentification à deux facteurs désactivée.',

    s3titre: 'Codes de récupération',
    descCodes:
      "Ces codes permettent de récupérer l'accès à votre compte si vous perdez votre application d'authentification. Chaque code ne peut être utilisé qu'une seule fois.",
    btnAfficherCodes: 'Afficher les codes',
    btnCacherCodes: 'Masquer',
    avertissementCodes: 'Gardez ces codes en lieu sûr, hors ligne de préférence.',
    aucunCode:
      'Aucun code available. Désactivez puis réactivez le 2FA pour en générer de nouveaux.',

    s4titre: 'Historique des connexions',
    historiqueIndispo: "L'historique des connexions sera available dans une prochaine version.",

    s5titre: 'Supprimer mon compte',
    s5desc:
      'La suppression est définitive. Vos contrats, transactions et évaluations passés sont conservés pour des raisons légales, mais votre profil ne sera plus accessible et votre adresse courriel sera libérée.',
    s5labelMdp: 'Confirmez votre mot de passe',
    s5btnSupprimer: 'Supprimer mon compte',
    s5btnEnCours: 'Suppression…',
    s5confirmDouble: 'Cette action est définitive. Êtes-vous sûr ?',
    s5succes: 'Compte supprimé. Redirection…',
    s5erreurMdp: 'Mot de passe incorrect.',
    s5erreurGenerique: 'Une erreur est survenue. Réessayez.',

    errGenerique: 'Une erreur est survenue. Réessayez.',
  },
  en: {
    title: 'Security',
    retour: '← My profile',
    chargement: 'Loading…',

    s1titre: 'Password',
    labelAncien: 'Current password',
    labelNouveau: 'New password',
    labelConfirmer: 'Confirm new password',
    btnChanger: 'Change password',
    chargementMdp: 'Updating…',
    succesMdp: 'Password updated successfully.',
    errMdpCorrespond: 'Passwords do not match.',
    errMdpCourt: 'New password must be at least 8 characters.',

    s2titre: 'Two-factor authentication (2FA)',
    badge2FAActif: '✓ 2FA enabled',
    badge2FAInactif: '2FA disabled',
    desc2FAInactif:
      'Add an extra layer of security to your account. Once enabled, every login will require a code from your authentication app.',
    desc2FAActif: 'Your account is protected by a TOTP app (Google Authenticator, Authy, etc.).',
    obligatoire: '⚠ Required for this role — cannot be disabled.',
    btnActiver: 'Enable 2FA',
    btnDesactiver: 'Disable 2FA',
    btnAnnuler: 'Cancel',

    instrQR: 'Scan this QR code with your TOTP app (Google Authenticator, Authy, 1Password, etc.).',
    instrQRSuite: 'Your app will display a 6-digit code that refreshes every 30 seconds.',
    btnJaiScan: "I've scanned the code →",
    instrCode: 'Enter the 6-digit code currently shown in your app to confirm activation.',
    placeholder2FA: '6-digit code',
    btnActiver2FA: 'Enable',

    titreCodesOk: '2FA enabled!',
    instrCodesOk:
      'Store these recovery codes safely — preferably offline. Each code can only be used once if you lose access to your app.',
    btnTerminer: 'Done',

    instrDesactiver: 'Enter the code currently shown in your authentication app to disable 2FA.',
    btnConfirmerDesactiver: 'Confirm deactivation',
    desactivation2FASucces: 'Two-factor authentication disabled.',

    s3titre: 'Recovery codes',
    descCodes:
      'These codes allow you to regain access to your account if you lose your authentication app. Each code can only be used once.',
    btnAfficherCodes: 'Show codes',
    btnCacherCodes: 'Hide',
    avertissementCodes: 'Keep these codes safe, preferably offline.',
    aucunCode: 'No codes available. Disable then re-enable 2FA to generate new ones.',

    s4titre: 'Login history',
    historiqueIndispo: 'Login history will be available in a future update.',

    s5titre: 'Delete my account',
    s5desc:
      'Deletion is permanent. Your past contracts, transactions and ratings are preserved for legal reasons, but your profile will no longer be accessible and your email will be freed.',
    s5labelMdp: 'Confirm your password',
    s5btnSupprimer: 'Delete my account',
    s5btnEnCours: 'Deleting…',
    s5confirmDouble: 'This action is permanent. Are you sure?',
    s5succes: 'Account deleted. Redirecting…',
    s5erreurMdp: 'Incorrect password.',
    s5erreurGenerique: 'An error occurred. Please try again.',

    errGenerique: 'An error occurred. Please try again.',
  },
};

// ── Composant ─────────────────────────────────────────────────────────────────

export default function Securite() {
  const { lang } = useContext(LangContext);
  const t = T[lang];
  const { utilisateur: authUser, deconnecter } = useAuth();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [chargementPage, setChargementPage] = useState(true);

  // Mot de passe
  const [formMdp, setFormMdp] = useState({ ancien: '', nouveau: '', confirmer: '' });
  const [chargementMdp, setChargementMdp] = useState(false);
  const [succesMdp, setSuccesMdp] = useState('');
  const [erreurMdp, setErreurMdp] = useState('');
  const [showAncien, setShowAncien] = useState(false);
  const [showNouveau, setShowNouveau] = useState(false);

  // 2FA
  const [etape2FA, setEtape2FA] = useState('repos');
  // 'repos' | 'setup_qr' | 'setup_code' | 'setup_ok' | 'desactiver_confirm'
  const [qrCode, setQrCode] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [codesActivation, setCodesActivation] = useState(null);
  const [chargement2FA, setChargement2FA] = useState(false);
  const [erreur2FA, setErreur2FA] = useState('');
  const [msg2FA, setMsg2FA] = useState('');

  // Recovery codes section
  const [afficherCodes, setAfficherCodes] = useState(false);

  // Suppression de compte
  const [supprMdp, setSupprMdp] = useState('');
  const [supprLoading, setSupprLoading] = useState(false);
  const [supprError, setSupprError] = useState('');
  const [supprSucces, setSupprSucces] = useState('');

  // ── Chargement profil ────────────────────────────────────────────────────
  const chargerProfil = useCallback(async () => {
    setChargementPage(true);
    try {
      const rep = await api.get('/auth/me');
      setUserData(rep.data);
    } catch {
      if (authUser) setUserData(authUser);
    } finally {
      setChargementPage(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    chargerProfil();
  }, [chargerProfil]);

  // ── Supprimer le compte ──────────────────────────────────────────────────
  const handleSupprimerCompte = async (e) => {
    e.preventDefault();
    setSupprError('');
    if (!supprMdp) {
      setSupprError(t.s5erreurMdp);
      return;
    }
    // Double-confirm natif — irréversible, on veut un dernier garde-fou.
    if (!window.confirm(t.s5confirmDouble)) return;

    setSupprLoading(true);
    try {
      await api.delete('/auth/me', { data: { password_hash: supprMdp } });
      setSupprSucces(t.s5succes);
      // Délai court pour laisser le user voir le message de succès,
      // puis logout + redirect home.
      setTimeout(() => {
        deconnecter();
        navigate('/');
      }, 1500);
    } catch (err) {
      const status = err?.response?.status;
      setSupprError(status === 401 ? t.s5erreurMdp : t.s5erreurGenerique);
    } finally {
      setSupprLoading(false);
    }
  };

  // ── Changer mot de passe ────────────────────────────────────────────────
  const handleChangerMdp = async (e) => {
    e.preventDefault();
    setSuccesMdp('');
    setErreurMdp('');
    if (formMdp.nouveau !== formMdp.confirmer) {
      setErreurMdp(t.errMdpCorrespond);
      return;
    }
    if (formMdp.nouveau.length < 8) {
      setErreurMdp(t.errMdpCourt);
      return;
    }
    setChargementMdp(true);
    try {
      await api.put('/auth/mot-de-passe', {
        ancien_mot_de_passe: formMdp.ancien,
        nouveau_mot_de_passe: formMdp.nouveau,
      });
      setSuccesMdp(t.succesMdp);
      setFormMdp({ ancien: '', nouveau: '', confirmer: '' });
    } catch (err) {
      setErreurMdp(err.response?.data?.error || t.errGenerique);
    } finally {
      setChargementMdp(false);
    }
  };

  // ── 2FA : lancer le setup ────────────────────────────────────────────────
  const handleActiver2FA = async () => {
    setChargement2FA(true);
    setErreur2FA('');
    try {
      const res = await api.post('/auth/2fa/setup', {});
      setQrCode(res.data.qr_code);
      setEtape2FA('setup_qr');
    } catch (err) {
      setErreur2FA(err.response?.data?.error || t.errGenerique);
    } finally {
      setChargement2FA(false);
    }
  };

  // ── 2FA : confirmer avec le code TOTP ───────────────────────────────────
  const handleConfirmerCode = async () => {
    if (codeInput.length !== 6) return;
    setChargement2FA(true);
    setErreur2FA('');
    try {
      const res = await api.post('/auth/2fa/activer', { token: codeInput });
      setCodesActivation(res.data.codes_recuperation);
      setCodeInput('');
      setEtape2FA('setup_ok');
    } catch (err) {
      setErreur2FA(err.response?.data?.error || t.errGenerique);
    } finally {
      setChargement2FA(false);
    }
  };

  // ── 2FA : terminer le setup ──────────────────────────────────────────────
  const handleTerminer2FA = async () => {
    setCodesActivation(null);
    setQrCode(null);
    setEtape2FA('repos');
    await chargerProfil();
  };

  // ── 2FA : désactiver ─────────────────────────────────────────────────────
  const handleDesactiver2FA = async () => {
    if (codeInput.length !== 6) return;
    setChargement2FA(true);
    setErreur2FA('');
    try {
      await api.post('/auth/2fa/desactiver', { token: codeInput });
      setCodeInput('');
      setEtape2FA('repos');
      setMsg2FA(t.desactivation2FASucces);
      setAfficherCodes(false);
      await chargerProfil();
      setTimeout(() => setMsg2FA(''), 4000);
    } catch (err) {
      setErreur2FA(err.response?.data?.error || t.errGenerique);
    } finally {
      setChargement2FA(false);
    }
  };

  const annuler2FA = () => {
    setEtape2FA('repos');
    setCodeInput('');
    setErreur2FA('');
    setQrCode(null);
  };

  // ── Données dérivées ─────────────────────────────────────────────────────
  const est2FAActif = !!userData?.two_factor_enabled;
  const obligatoire2FA = ROLES_2FA_OBLIGATOIRE.has(userData?.user_type);

  // ── Styles ───────────────────────────────────────────────────────────────
  const s = {
    page: { minHeight: '100vh', background: 'var(--couleur-fond)', color: 'var(--couleur-texte)' },
    main: { maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' },
    h1: {
      fontFamily: 'Georgia, serif',
      fontSize: '1.85rem',
      fontWeight: 700,
      color: 'var(--couleur-or)',
      marginBottom: '2rem',
      paddingBottom: '0.6rem',
      borderBottom: '1px solid rgba(184,134,11,0.22)',
    },
    carte: {
      background: 'var(--couleur-fond-carte)',
      border: '1px solid var(--couleur-bordure-forte)',
      borderRadius: '10px',
      padding: '1.5rem 1.8rem',
      marginBottom: '1.3rem',
    },
    titreCarte: {
      fontFamily: 'Georgia, serif',
      fontSize: '1rem',
      fontWeight: 700,
      color: 'var(--couleur-or)',
      marginBottom: '1.2rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.79rem',
      color: 'rgba(232,224,208,0.55)',
      marginBottom: '0.25rem',
      fontWeight: 500,
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
    },
    inputWrap: { position: 'relative' },
    input: {
      background: 'var(--couleur-fond-carte)',
      border: '1px solid var(--couleur-bordure-forte)',
      borderRadius: '6px',
      color: 'var(--couleur-texte)',
      fontSize: '0.95rem',
      padding: '0.55rem 2.6rem 0.55rem 0.8rem',
      width: '100%',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box',
    },
    eyeBtn: {
      position: 'absolute',
      right: '0.6rem',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      color: 'rgba(232,224,208,0.35)',
      cursor: 'pointer',
      fontSize: '0.85rem',
      padding: '0.2rem',
      lineHeight: 1,
      transition: 'color 0.15s',
    },
    grille2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
    btnOr: {
      background: 'var(--couleur-or)',
      border: 'none',
      borderRadius: '6px',
      color: 'var(--couleur-fond-carte)',
      cursor: 'pointer',
      fontSize: '0.93rem',
      fontWeight: 700,
      padding: '0.6rem 1.5rem',
      transition: 'filter 0.15s',
    },
    btnContour: {
      background: 'transparent',
      border: '1px solid var(--couleur-or)',
      borderRadius: '6px',
      color: 'var(--couleur-or)',
      cursor: 'pointer',
      fontSize: '0.88rem',
      fontWeight: 600,
      padding: '0.45rem 1.1rem',
      transition: 'background 0.15s',
    },
    btnGris: {
      background: 'transparent',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: '6px',
      color: 'rgba(232,224,208,0.5)',
      cursor: 'pointer',
      fontSize: '0.85rem',
      padding: '0.45rem 1rem',
      transition: 'border-color 0.15s',
    },
    btnDanger: {
      background: 'transparent',
      border: '1px solid rgba(200,60,60,0.5)',
      borderRadius: '6px',
      color: 'var(--couleur-texte-erreur)',
      cursor: 'pointer',
      fontSize: '0.88rem',
      fontWeight: 600,
      padding: '0.45rem 1.1rem',
      transition: 'background 0.15s',
    },
    msgSucces: {
      background: 'rgba(26,74,26,0.4)',
      border: '1px solid rgba(26,200,26,0.25)',
      borderRadius: '6px',
      color: 'var(--couleur-texte-succes)',
      padding: '0.65rem 1rem',
      marginBottom: '1rem',
      fontSize: '0.9rem',
    },
    msgErreur: {
      background: 'rgba(120,20,20,0.35)',
      border: '1px solid rgba(200,40,40,0.35)',
      borderRadius: '6px',
      color: 'var(--couleur-texte-erreur)',
      padding: '0.65rem 1rem',
      marginBottom: '1rem',
      fontSize: '0.9rem',
    },
    badge2FAActif: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.3rem',
      background: 'rgba(26,120,26,0.25)',
      border: '1px solid rgba(40,180,40,0.3)',
      color: 'var(--couleur-texte-succes)',
      borderRadius: '20px',
      padding: '0.18rem 0.8rem',
      fontSize: '0.82rem',
      fontWeight: 600,
    },
    badge2FAInactif: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.3rem',
      background: 'rgba(80,80,80,0.18)',
      border: '1px solid rgba(120,120,120,0.25)',
      color: 'rgba(232,224,208,0.4)',
      borderRadius: '20px',
      padding: '0.18rem 0.8rem',
      fontSize: '0.82rem',
    },
    codeInputStyle: {
      background: 'var(--couleur-fond-carte)',
      border: '1px solid var(--couleur-bordure-forte)',
      borderRadius: '6px',
      color: 'var(--couleur-texte)',
      fontSize: '1.4rem',
      letterSpacing: '0.35em',
      textAlign: 'center',
      padding: '0.6rem 0.8rem',
      width: '100%',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box',
      fontFamily: 'monospace',
    },
    codeBadge: {
      fontFamily: 'monospace',
      fontSize: '0.88rem',
      letterSpacing: '0.1em',
      color: 'var(--couleur-or)',
      background: 'rgba(184,134,11,0.08)',
      border: '1px solid rgba(184,134,11,0.2)',
      borderRadius: '4px',
      padding: '0.35rem 0.6rem',
      userSelect: 'all',
    },
  };

  // ── Spinner ──────────────────────────────────────────────────────────────
  if (chargementPage && !userData) {
    return (
      <PrivateLayout>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
          }}
        >
          <span
            style={{ color: 'rgba(255,255,255,0.2)', fontSize: '1.5rem', letterSpacing: '0.2em' }}
          >
            ···
          </span>
        </div>
      </PrivateLayout>
    );
  }

  // ── Rendu principal ──────────────────────────────────────────────────────
  return (
    <PrivateLayout>
      <main style={s.main}>
        <PageHeader icon="🔒" title={t.title} />

        {/* ── Section 1 : Mot de passe ─────────────────────────────────── */}
        <div style={s.carte}>
          <div style={s.titreCarte}>
            <span>🔑</span>
            {t.s1titre}
          </div>

          {succesMdp && <div style={s.msgSucces}>{succesMdp}</div>}
          {erreurMdp && <div style={s.msgErreur}>{erreurMdp}</div>}

          <form onSubmit={handleChangerMdp} noValidate>
            <div style={{ marginBottom: '1rem' }}>
              <label style={s.label}>{t.labelAncien}</label>
              <div style={s.inputWrap}>
                <input
                  type={showAncien ? 'text' : 'password'}
                  value={formMdp.ancien}
                  onChange={(e) => setFormMdp((f) => ({ ...f, ancien: e.target.value }))}
                  style={s.input}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)')
                  }
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  style={s.eyeBtn}
                  onClick={() => setShowAncien((v) => !v)}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--couleur-or)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(232,224,208,0.35)')}
                >
                  {showAncien ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <div style={{ ...s.grille2, marginBottom: '1.2rem' }}>
              <div>
                <label style={s.label}>{t.labelNouveau}</label>
                <div style={s.inputWrap}>
                  <input
                    type={showNouveau ? 'text' : 'password'}
                    value={formMdp.nouveau}
                    onChange={(e) => setFormMdp((f) => ({ ...f, nouveau: e.target.value }))}
                    style={s.input}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)')
                    }
                    autoComplete="new-password"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    style={s.eyeBtn}
                    onClick={() => setShowNouveau((v) => !v)}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--couleur-or)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(232,224,208,0.35)')}
                  >
                    {showNouveau ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
              <div>
                <label style={s.label}>{t.labelConfirmer}</label>
                <input
                  type="password"
                  value={formMdp.confirmer}
                  onChange={(e) => setFormMdp((f) => ({ ...f, confirmer: e.target.value }))}
                  style={{ ...s.input, padding: '0.55rem 0.8rem' }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)')
                  }
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={chargementMdp || !formMdp.ancien || !formMdp.nouveau}
              style={{
                ...s.btnOr,
                opacity: chargementMdp || !formMdp.ancien || !formMdp.nouveau ? 0.55 : 1,
                cursor:
                  chargementMdp || !formMdp.ancien || !formMdp.nouveau ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!chargementMdp) e.currentTarget.style.filter = 'brightness(1.1)';
              }}
              onMouseLeave={(e) => (e.currentTarget.style.filter = 'brightness(1)')}
            >
              {chargementMdp ? t.chargementMdp : t.btnChanger}
            </button>
          </form>
        </div>

        {/* ── Section 2 : 2FA ─────────────────────────────────────────── */}
        <div style={s.carte}>
          <div
            style={{
              ...s.titreCarte,
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '0.5rem',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📱</span>
              {t.s2titre}
            </span>
            <span style={est2FAActif ? s.badge2FAActif : s.badge2FAInactif}>
              {est2FAActif ? t.badge2FAActif : t.badge2FAInactif}
            </span>
          </div>

          {msg2FA && <div style={s.msgSucces}>{msg2FA}</div>}

          {/* ── repos : 2FA désactivé ── */}
          {etape2FA === 'repos' && !est2FAActif && (
            <div>
              <p
                style={{
                  fontSize: '0.9rem',
                  color: 'rgba(232,224,208,0.6)',
                  lineHeight: 1.6,
                  marginBottom: '1.2rem',
                }}
              >
                {t.desc2FAInactif}
              </p>
              {erreur2FA && <div style={s.msgErreur}>{erreur2FA}</div>}
              <button
                onClick={handleActiver2FA}
                disabled={chargement2FA}
                style={{
                  ...s.btnOr,
                  opacity: chargement2FA ? 0.6 : 1,
                  cursor: chargement2FA ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!chargement2FA) e.currentTarget.style.filter = 'brightness(1.1)';
                }}
                onMouseLeave={(e) => (e.currentTarget.style.filter = 'brightness(1)')}
              >
                {chargement2FA ? '···' : t.btnActiver}
              </button>
            </div>
          )}

          {/* ── repos : 2FA activé ── */}
          {etape2FA === 'repos' && est2FAActif && (
            <div>
              <p
                style={{
                  fontSize: '0.9rem',
                  color: 'rgba(232,224,208,0.6)',
                  lineHeight: 1.6,
                  marginBottom: '1.2rem',
                }}
              >
                {t.desc2FAActif}
              </p>
              {obligatoire2FA ? (
                <p
                  style={{
                    fontSize: '0.84rem',
                    color: 'rgba(240,160,80,0.85)',
                    background: 'rgba(180,100,20,0.1)',
                    border: '1px solid rgba(180,100,20,0.25)',
                    borderRadius: '6px',
                    padding: '0.6rem 0.9rem',
                  }}
                >
                  {t.obligatoire}
                </p>
              ) : (
                <button
                  onClick={() => {
                    setEtape2FA('desactiver_confirm');
                    setErreur2FA('');
                  }}
                  style={s.btnDanger}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(200,60,60,0.08)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {t.btnDesactiver}
                </button>
              )}
            </div>
          )}

          {/* ── Étape : QR Code ── */}
          {etape2FA === 'setup_qr' && (
            <div>
              <p
                style={{
                  fontSize: '0.88rem',
                  color: 'rgba(232,224,208,0.65)',
                  lineHeight: 1.6,
                  marginBottom: '0.4rem',
                }}
              >
                {t.instrQR}
              </p>
              <p
                style={{
                  fontSize: '0.82rem',
                  color: 'rgba(232,224,208,0.4)',
                  lineHeight: 1.5,
                  marginBottom: '1.2rem',
                }}
              >
                {t.instrQRSuite}
              </p>
              {qrCode ? (
                <div style={{ textAlign: 'center', marginBottom: '1.4rem' }}>
                  <img
                    src={qrCode}
                    alt="QR Code 2FA"
                    style={{
                      width: '200px',
                      height: '200px',
                      borderRadius: '10px',
                      border: '2px solid var(--couleur-bordure-forte)',
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    color: 'rgba(255,255,255,0.15)',
                    fontSize: '1.2rem',
                    marginBottom: '1.4rem',
                    padding: '3rem 0',
                  }}
                >
                  ···
                </div>
              )}
              {erreur2FA && <div style={s.msgErreur}>{erreur2FA}</div>}
              <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
                <button
                  style={s.btnGris}
                  onClick={annuler2FA}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')
                  }
                >
                  {t.btnAnnuler}
                </button>
                <button
                  style={s.btnOr}
                  onClick={() => {
                    setEtape2FA('setup_code');
                    setErreur2FA('');
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.filter = 'brightness(1.1)')}
                  onMouseLeave={(e) => (e.currentTarget.style.filter = 'brightness(1)')}
                >
                  {t.btnJaiScan}
                </button>
              </div>
            </div>
          )}

          {/* ── Étape : Code TOTP ── */}
          {etape2FA === 'setup_code' && (
            <div>
              <p
                style={{
                  fontSize: '0.88rem',
                  color: 'rgba(232,224,208,0.65)',
                  lineHeight: 1.6,
                  marginBottom: '1.2rem',
                }}
              >
                {t.instrCode}
              </p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, ''))}
                placeholder={t.placeholder2FA}
                style={s.codeInputStyle}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)')}
                autoFocus
              />
              {erreur2FA && (
                <div style={{ ...s.msgErreur, marginTop: '0.8rem', marginBottom: 0 }}>
                  {erreur2FA}
                </div>
              )}
              <div
                style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginTop: '1.1rem' }}
              >
                <button
                  style={s.btnGris}
                  onClick={() => {
                    setEtape2FA('setup_qr');
                    setErreur2FA('');
                    setCodeInput('');
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')
                  }
                >
                  ←
                </button>
                <button
                  onClick={handleConfirmerCode}
                  disabled={chargement2FA || codeInput.length !== 6}
                  style={{
                    ...s.btnOr,
                    opacity: chargement2FA || codeInput.length !== 6 ? 0.55 : 1,
                    cursor: chargement2FA || codeInput.length !== 6 ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (codeInput.length === 6 && !chargement2FA)
                      e.currentTarget.style.filter = 'brightness(1.1)';
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.filter = 'brightness(1)')}
                >
                  {chargement2FA ? '···' : t.btnActiver2FA}
                </button>
              </div>
            </div>
          )}

          {/* ── Étape : Codes de récupération (post-activation) ── */}
          {etape2FA === 'setup_ok' && (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem',
                  marginBottom: '1rem',
                }}
              >
                <span style={{ fontSize: '1.8rem' }}>✅</span>
                <span
                  style={{
                    fontFamily: 'Georgia, serif',
                    fontSize: '1.05rem',
                    color: 'var(--couleur-texte-succes)',
                    fontWeight: 700,
                  }}
                >
                  {t.titreCodesOk}
                </span>
              </div>
              <p
                style={{
                  fontSize: '0.87rem',
                  color: 'rgba(232,224,208,0.6)',
                  lineHeight: 1.65,
                  marginBottom: '1.2rem',
                }}
              >
                {t.instrCodesOk}
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.5rem',
                  background: 'rgba(0,0,0,0.25)',
                  border: '1px solid rgba(184,134,11,0.15)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.2rem',
                }}
              >
                {(codesActivation || []).map((code, i) => (
                  <code key={i} style={s.codeBadge}>
                    {code}
                  </code>
                ))}
              </div>
              <button
                style={s.btnOr}
                onClick={handleTerminer2FA}
                onMouseEnter={(e) => (e.currentTarget.style.filter = 'brightness(1.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.filter = 'brightness(1)')}
              >
                {t.btnTerminer}
              </button>
            </div>
          )}

          {/* ── Étape : Confirmation désactivation ── */}
          {etape2FA === 'desactiver_confirm' && (
            <div>
              <p
                style={{
                  fontSize: '0.88rem',
                  color: 'rgba(232,224,208,0.65)',
                  lineHeight: 1.6,
                  marginBottom: '1.2rem',
                }}
              >
                {t.instrDesactiver}
              </p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, ''))}
                placeholder={t.placeholder2FA}
                style={s.codeInputStyle}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)')}
                autoFocus
              />
              {erreur2FA && (
                <div style={{ ...s.msgErreur, marginTop: '0.8rem', marginBottom: 0 }}>
                  {erreur2FA}
                </div>
              )}
              <div
                style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginTop: '1.1rem' }}
              >
                <button
                  style={s.btnGris}
                  onClick={annuler2FA}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')
                  }
                >
                  {t.btnAnnuler}
                </button>
                <button
                  onClick={handleDesactiver2FA}
                  disabled={chargement2FA || codeInput.length !== 6}
                  style={{
                    ...s.btnDanger,
                    opacity: chargement2FA || codeInput.length !== 6 ? 0.55 : 1,
                    cursor: chargement2FA || codeInput.length !== 6 ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (codeInput.length === 6)
                      e.currentTarget.style.background = 'rgba(200,60,60,0.08)';
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {chargement2FA ? '···' : t.btnConfirmerDesactiver}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Section 3 : Codes de récupération (si 2FA active) ────────── */}
        {est2FAActif && etape2FA === 'repos' && (
          <div style={s.carte}>
            <div
              style={{
                ...s.titreCarte,
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>🗝️</span>
                {t.s3titre}
              </span>
              <button
                style={s.btnContour}
                onClick={() => setAfficherCodes((v) => !v)}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(184,134,11,0.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {afficherCodes ? t.btnCacherCodes : t.btnAfficherCodes}
              </button>
            </div>

            <p
              style={{
                fontSize: '0.88rem',
                color: 'rgba(232,224,208,0.55)',
                lineHeight: 1.6,
                marginBottom: afficherCodes ? '1rem' : 0,
              }}
            >
              {t.descCodes}
            </p>

            {afficherCodes && (
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: 'rgba(180,100,20,0.1)',
                    border: '1px solid rgba(180,100,20,0.25)',
                    borderRadius: '6px',
                    padding: '0.6rem 0.9rem',
                    marginBottom: '1rem',
                    fontSize: '0.82rem',
                    color: 'rgba(240,160,80,0.85)',
                  }}
                >
                  <span>⚠</span>
                  <span>{t.avertissementCodes}</span>
                </div>
                {userData?.two_factor_recovery_codes?.length ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '0.5rem',
                      background: 'rgba(0,0,0,0.2)',
                      border: '1px solid rgba(184,134,11,0.1)',
                      borderRadius: '8px',
                      padding: '1rem',
                    }}
                  >
                    {userData.two_factor_recovery_codes.map((code, i) => (
                      <code key={i} style={s.codeBadge}>
                        {code}
                      </code>
                    ))}
                  </div>
                ) : (
                  <p
                    style={{
                      fontSize: '0.85rem',
                      color: 'rgba(232,224,208,0.35)',
                      fontStyle: 'italic',
                    }}
                  >
                    {t.aucunCode}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Section 4 : Historique des connexions ──────────────────── */}
        <div style={s.carte}>
          <div style={s.titreCarte}>
            <span>📋</span>
            {t.s4titre}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.7rem',
              background: 'rgba(28,58,28,0.25)',
              border: '1px solid rgba(28,58,28,0.6)',
              borderRadius: '8px',
              padding: '1rem 1.2rem',
              fontSize: '0.87rem',
              color: 'rgba(232,224,208,0.4)',
            }}
          >
            <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>🚧</span>
            <span>{t.historiqueIndispo}</span>
          </div>
        </div>

        {/* ── Section 5 : Supprimer mon compte ────────────────────────── */}
        <div style={{ ...s.carte, marginTop: '2rem', borderColor: 'rgba(220, 53, 69, 0.45)' }}>
          <h2 style={{ ...s.titreCarte, color: '#ff8a95' }}>{t.s5titre}</h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>{t.s5desc}</p>

          {supprSucces ? (
            <div
              style={{
                marginTop: '1rem',
                padding: '0.9rem',
                background: 'rgba(46, 204, 113, 0.12)',
                border: '1px solid rgba(46, 204, 113, 0.4)',
                borderRadius: '6px',
                color: '#a3e9c5',
                fontSize: '0.92rem',
              }}
            >
              {supprSucces}
            </div>
          ) : (
            <form
              onSubmit={handleSupprimerCompte}
              style={{ marginTop: '1rem', display: 'grid', gap: '0.8rem' }}
            >
              <label>
                <div style={{ marginBottom: '0.35rem', fontSize: '0.86rem' }}>{t.s5labelMdp}</div>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={supprMdp}
                  onChange={(e) => setSupprMdp(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    borderRadius: '6px',
                    color: 'var(--couleur-texte)',
                    padding: '0.7rem 0.9rem',
                    fontSize: '0.92rem',
                  }}
                />
              </label>
              {supprError && (
                <div style={{ color: '#ff6b6b', fontSize: '0.86rem' }}>{supprError}</div>
              )}
              <button
                type="submit"
                disabled={supprLoading}
                style={{
                  padding: '0.75rem 1.2rem',
                  background: '#c0392b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: supprLoading ? 'wait' : 'pointer',
                  width: 'fit-content',
                }}
              >
                {supprLoading ? t.s5btnEnCours : t.s5btnSupprimer}
              </button>
            </form>
          )}
        </div>
      </main>
    </PrivateLayout>
  );
}
